/**
 * Owns: mirroring cart.attributes.store_code to line-item _store_code.
 * Exposes: getSelectedStoreCode, syncStoreCodeToLineItems, clearStoreCodeFromLineItems.
 * Consumes: store-pricing, store-locator-cart, store-events, utilities, events.
 */

import { CartUpdateEvent, ThemeEvents } from '@theme/events';
import { STORE_LOCATOR_SOURCE } from '@theme/store-events';
import { fetchCartAttributes } from '@theme/store-pricing';
import { fetchCartJson } from '@theme/store-locator-cart';
import { debounce, fetchConfig, onDocumentReady } from '@theme/utilities';
import { cartQueue } from '@theme/cart-queue';

const STORE_CONTEXT_SOURCE = 'store-context';

/** @type {string | null} */
let cachedStoreCode = null;

/** @type {boolean} */
let syncInFlight = false;

/**
 * @returns {Promise<string | null>}
 */
export async function getSelectedStoreCode() {
  if (cachedStoreCode) {
    return cachedStoreCode;
  }

  try {
    const attributes = await fetchCartAttributes();
    const storeCode = attributes.store_code;
    if (storeCode) {
      const trimmed = String(storeCode).trim();
      cachedStoreCode = trimmed || null;
      return cachedStoreCode;
    }
  } catch (error) {
    console.error('Failed to read store_code from cart attributes:', error);
  }

  return null;
}

/**
 * Syncs _store_code to all shipping line items. Returns true if any line was changed.
 * @param {string} storeCode
 * @returns {Promise<boolean>}
 */
export async function syncStoreCodeToLineItems(storeCode) {
  const normalizedStoreCode = String(storeCode ?? '').trim();
  if (!normalizedStoreCode) {
    return false;
  }

  return cartQueue.enqueue(async () => {
    const cart = /** @type {{ items?: Array<{ key: string; quantity: number; requires_shipping: boolean; properties?: Record<string, string> }> } | null} */ (
      await fetchCartJson()
    );
    if (!cart) return false;

    let changed = false;

    for (const item of cart.items ?? []) {
      if (!item.requires_shipping) {
        continue;
      }

      const currentStoreCode = item.properties?._store_code ?? item.properties?.store_code;
      if (currentStoreCode === normalizedStoreCode) {
        continue;
      }

      const body = JSON.stringify({
        id: item.key,
        quantity: item.quantity,
        properties: {
          ...(item.properties ?? {}),
          _store_code: normalizedStoreCode,
        },
      });

      try {
        const response = await fetch(Theme.routes.cart_change_url, fetchConfig('json', { body }));
        if (!response.ok) {
          console.error('[store-context] cart/change failed for line', item.key, response.status);
          continue;
        }
        changed = true;
      } catch (error) {
        console.error('[store-context] cart/change error for line', item.key, error);
      }
    }

    return changed;
  });
}

/**
 * Strips _store_code from all shipping line items. Returns true if any line was changed.
 * @returns {Promise<boolean>}
 */
export async function clearStoreCodeFromLineItems() {
  return cartQueue.enqueue(async () => {
    const cart = /** @type {{ items?: Array<{ key: string; quantity: number; requires_shipping: boolean; properties?: Record<string, string> }> } | null} */ (
      await fetchCartJson()
    );
    if (!cart) return false;

    let changed = false;

    for (const item of cart.items ?? []) {
      if (!item.requires_shipping) {
        continue;
      }

      const hasProperty = item.properties?._store_code != null || item.properties?.store_code != null;
      if (!hasProperty) {
        continue;
      }

      const cleanedProperties = { ...(item.properties ?? {}) };
      delete cleanedProperties._store_code;
      delete cleanedProperties.store_code;

      const body = JSON.stringify({
        id: item.key,
        quantity: item.quantity,
        properties: cleanedProperties,
      });

      try {
        const response = await fetch(Theme.routes.cart_change_url, fetchConfig('json', { body }));
        if (!response.ok) {
          console.error('[store-context] cart/change clear failed for line', item.key, response.status);
          continue;
        }
        changed = true;
      } catch (error) {
        console.error('[store-context] cart/change clear error for line', item.key, error);
      }
    }

    return changed;
  });
}

/**
 * @param {boolean} changed
 */
async function dispatchCartRefreshIfChanged(changed) {
  if (!changed) return;

  const cart = /** @type {{ item_count?: number } | null} */ (await fetchCartJson());
  if (cart) {
    document.dispatchEvent(
      new CartUpdateEvent(cart, STORE_CONTEXT_SOURCE, {
        source: STORE_CONTEXT_SOURCE,
        itemCount: cart.item_count,
      })
    );
  }
}

/**
 * @param {Event} event
 */
async function handleLocatorCartUpdate(event) {
  const detail = /** @type {{ resource?: { attributes?: Record<string, string> }; data?: { source?: string } } | undefined} */ (
    /** @type {any} */ (event).detail
  );

  if (detail?.data?.source !== STORE_LOCATOR_SOURCE) {
    return;
  }

  const storeCode = String(detail?.resource?.attributes?.store_code ?? '').trim() || null;
  cachedStoreCode = storeCode;

  if (syncInFlight) return;
  syncInFlight = true;

  try {
    let changed;
    if (storeCode) {
      changed = await syncStoreCodeToLineItems(storeCode);
    } else {
      changed = await clearStoreCodeFromLineItems();
    }
    await dispatchCartRefreshIfChanged(changed);
  } catch (error) {
    console.error('[store-context] Failed to sync line item properties after store change:', error);
  } finally {
    syncInFlight = false;
  }
}

const debouncedHandleLocatorCartUpdate = debounce(handleLocatorCartUpdate, 200);

async function syncFromCartAttributes() {
  const storeCode = await getSelectedStoreCode();
  if (storeCode) {
    const changed = await syncStoreCodeToLineItems(storeCode);
    await dispatchCartRefreshIfChanged(changed);
  }
}

if (typeof document !== 'undefined') {
  document.addEventListener(ThemeEvents.cartUpdate, debouncedHandleLocatorCartUpdate);

  onDocumentReady(() => {
    syncFromCartAttributes().catch((error) => {
      console.error('[store-context] Failed to sync store_code line item properties:', error);
    });
  });
}
